/* =========================================================================
 * SerieFotosAdminModal — CAMASA registra séries fotográficas direto pelas
 * etapas da obra (sem moderação, sem assinatura, GPS opcional). Aumenta a
 * Cronologia Completude imediatamente.
 * ========================================================================= */
import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Camera, X, MapPin, CheckCircle2, Trash2 } from "lucide-react";
import { fileUrl } from "../lib";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authApi = (m, p, b) => {
  const t = localStorage.getItem("cps_token");
  return axios({ method: m, url: `${API}${p}`, data: b, headers: t ? { Authorization: `Bearer ${t}` } : {} }).then(r => r.data);
};

export default function SerieFotosAdminModal({ obra, etapaNome, onClose, onReload }) {
  const [series, setSeries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Recarrega obra para pegar series_fotos atualizadas
      const o = await authApi("get", `/obras/${obra.id}`);
      const filtradas = (o.series_fotos || []).filter(
        s => s.etapa_nome === etapaNome && (s.status === "aprovada" || s.status === "pendente")
      );
      setSeries(filtradas);
    } finally { setLoading(false); }
  }, [obra.id, etapaNome]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="modal-back" onClick={onClose} data-testid="serie-admin-modal">
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 820, width: "100%", maxHeight: "92vh", overflowY: "auto" }}>
        <div className="modal-head">
          <h3 className="modal-title">Fotos da etapa — <em>{etapaNome}</em></h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {!showForm && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <span style={{ fontSize: 11, letterSpacing: ".15em", textTransform: "uppercase", color: "var(--ink-3)" }}>
                  {series.length === 0 ? "Nenhuma série registrada nesta etapa" : `${series.length} série(s)`}
                </span>
                <button
                  onClick={() => setShowForm(true)}
                  data-testid="btn-nova-serie-admin"
                  className="btn btn-sm"
                  style={{ background: "var(--gold)", color: "#0a0a0a", fontWeight: 600 }}
                >
                  <Camera size={13} /> Nova série
                </button>
              </div>
              {loading ? (
                <div style={{ padding: 30, textAlign: "center", color: "var(--ink-3)" }}>Carregando…</div>
              ) : series.length === 0 ? (
                <div style={{ padding: 30, textAlign: "center", color: "var(--ink-3)", border: "1px dashed var(--line)" }}>
                  Clique em <strong>Nova série</strong> para documentar esta etapa com 3 fotos.
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
                  {series.map(s => (
                    <SerieCard key={s.id} serie={s} obraId={obra.id} onChanged={() => { load(); onReload?.(); }} />
                  ))}
                </div>
              )}
            </>
          )}
          {showForm && (
            <NovaSerieAdminForm
              obraId={obra.id}
              etapaNome={etapaNome}
              onCancel={() => setShowForm(false)}
              onSaved={() => { setShowForm(false); load(); onReload?.(); }}
            />
          )}
        </div>
      </div>
    </div>
  );
}


function SerieCard({ serie, obraId, onChanged }) {
  const aprovada = serie.status === "aprovada";
  const pendente = serie.status === "pendente";
  const isAdmin = serie.origem === "admin";
  const [busy, setBusy] = useState(false);
  const toggleSB = async () => {
    setBusy(true);
    try {
      const res = await authApi("patch", `/obras/${obraId}/series_fotos/${serie.id}`, { aprovada: !aprovada });
      // Dispara modal de notificação WhatsApp se virou aprovada (entrou no SB)
      if (res?.aprovada === true) {
        window.dispatchEvent(new CustomEvent("camasa:notificar-cliente", {
          detail: { obraId, motivo: "serie_aprovada" },
        }));
      }
      onChanged?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "Falha ao atualizar aprovação");
    } finally { setBusy(false); }
  };
  const apagar = async () => {
    if (!window.confirm(`Apagar série "${serie.item_descricao}"?\nEsta ação NÃO pode ser desfeita — as fotos serão removidas e Carlo precisará registrar a série novamente.`)) return;
    setBusy(true);
    try {
      await authApi("delete", `/obras/${obraId}/series_fotos/${serie.id}`);
      onChanged?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "Falha ao apagar série");
    } finally { setBusy(false); }
  };
  const nFotos = (serie.fotos || []).length;
  return (
    <div data-testid={`serie-admin-card-${serie.id}`} style={{ padding: 12, background: "var(--bg-3)", border: "1px solid var(--line)", borderLeft: `3px solid ${aprovada ? "#5fa86c" : "#d4b27a"}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6, gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-1)", wordBreak: "break-word", flex: 1, minWidth: 0 }}>{serie.item_descricao}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
          <span style={{ fontSize: 9, color: aprovada ? "#5fa86c" : "#d4b27a", padding: "2px 6px", border: `1px solid ${aprovada ? "#5fa86c55" : "#d4b27a55"}`, letterSpacing: ".15em", textTransform: "uppercase" }}>
            {aprovada ? "Aprovada" : pendente ? "Pendente" : serie.status}
          </span>
          <button
            onClick={apagar}
            disabled={busy}
            data-testid={`serie-admin-delete-${serie.id}`}
            title="Apagar série inteira"
            style={{ padding: 4, border: "1px solid var(--line)", background: "transparent", color: "#c47a7a", cursor: busy ? "wait" : "pointer", opacity: busy ? 0.5 : 1 }}
          >
            <X size={13} />
          </button>
        </div>
      </div>
      <div style={{ fontSize: 10, color: "var(--ink-3)", marginBottom: 8 }}>
        {isAdmin ? "📸 CAMASA" : `🤝 ${serie.parceiro_nome || "parceiro"}`}
        {" · "}{new Date(serie.enviado_em || Date.now()).toLocaleDateString("pt-BR")}
        {nFotos > 0 && <> · {nFotos} imagem{nFotos === 1 ? "" : "ns"}</>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(nFotos, 4)}, 1fr)`, gap: 4 }}>
        {(serie.fotos || []).map((f, idx) => (
          <a key={idx} href={fileUrl(f.url)} target="_blank" rel="noopener noreferrer" style={{ aspectRatio: "1/1", backgroundImage: `url(${fileUrl(f.url)})`, backgroundSize: "cover", backgroundPosition: "center", border: "1px solid var(--line)", position: "relative" }} title={`Imagem ${idx + 1}`}>
            <div style={{ position: "absolute", bottom: 2, left: 2, padding: "1px 5px", background: "rgba(0,0,0,0.7)", color: "var(--gold)", fontSize: 9, letterSpacing: ".1em", fontWeight: 600 }}>
              {idx + 1}
            </div>
          </a>
        ))}
      </div>
      {serie.endereco_aproximado && (
        <div style={{ marginTop: 6, fontSize: 10, color: "var(--ink-3)" }}>
          📍 {serie.endereco_aproximado}
        </div>
      )}
      {/* Toggle Signature Book */}
      <button
        onClick={toggleSB}
        disabled={busy}
        data-testid={`serie-toggle-sb-${serie.id}`}
        title={aprovada ? "Clique para remover do Signature Book" : "Clique para aprovar e exibir no Signature Book do cliente"}
        style={{
          marginTop: 10, width: "100%", padding: "8px 10px",
          background: aprovada ? "rgba(95,168,108,0.15)" : "var(--bg-2)",
          color: aprovada ? "#5fa86c" : "var(--ink-3)",
          border: `1px solid ${aprovada ? "#5fa86c66" : "var(--line)"}`,
          fontSize: 10, letterSpacing: ".18em", textTransform: "uppercase",
          fontWeight: 600, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        }}
      >
        {aprovada ? <><CheckCircle2 size={12} /> No Signature Book ✓</> : <>Não exibido · clique para aprovar</>}
      </button>
    </div>
  );
}


function NovaSerieAdminForm({ obraId, etapaNome, onCancel, onSaved }) {
  const [itemDesc, setItemDesc] = useState("");
  const [fotos, setFotos] = useState([]); // [{ file, previewUrl }]
  const [gps, setGps] = useState(null);
  const [gpsBusy, setGpsBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const inputAdd = useRef(null);

  const handleAddFotos = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const novas = files.slice(0, 12 - fotos.length).map(f => ({
      file: f,
      previewUrl: URL.createObjectURL(f),
    }));
    setFotos(prev => [...prev, ...novas]);
    if (inputAdd.current) inputAdd.current.value = "";
  };
  const removerFoto = (idx) => {
    setFotos(prev => {
      const out = [...prev];
      const [r] = out.splice(idx, 1);
      if (r?.previewUrl) URL.revokeObjectURL(r.previewUrl);
      return out;
    });
  };

  // GPS opcional — tenta auto-capturar ao montar
  useEffect(() => {
    if (!navigator.geolocation) return;
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude, lon = pos.coords.longitude;
        let endereco = `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
        try {
          const r = await axios.get(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&accept-language=pt-BR&zoom=12`);
          const a = r.data?.address || {};
          const cidade = a.city || a.town || a.village || a.suburb;
          const estado = a.state_code || a.state;
          endereco = [cidade, estado].filter(Boolean).join(", ") || endereco;
        } catch {}
        setGps({ lat, lon, endereco });
        setGpsBusy(false);
      },
      () => setGpsBusy(false),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 120000 },
    );
  }, []);

  const salvar = async () => {
    setErr("");
    if (!itemDesc.trim()) { setErr("Informe a descrição do item"); return; }
    if (fotos.length < 1) {
      setErr("Envie pelo menos 1 foto");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("etapa_nome", etapaNome);
      fd.append("item_descricao", itemDesc.trim());
      fotos.forEach(f => fd.append("fotos", f.file));
      fd.append("origem", "admin");
      if (gps) {
        fd.append("latitude", String(gps.lat));
        fd.append("longitude", String(gps.lon));
        fd.append("endereco_aproximado", gps.endereco);
      }
      const token = localStorage.getItem("cps_token");
      await axios.post(`${API}/obras/${obraId}/series_fotos`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Erro ao salvar");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="field" style={{ marginBottom: 14 }}>
        <label>Descrição do item *</label>
        <input
          className="input"
          value={itemDesc}
          onChange={e => setItemDesc(e.target.value.slice(0, 140))}
          placeholder="Ex: Bancada Banheiro Master · Visita de supervisão"
          data-testid="admin-serie-item-desc"
          autoFocus
        />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
        {fotos.map((f, idx) => (
          <div key={idx} style={{ position: "relative", aspectRatio: "1/1", border: "2px solid var(--gold)" }} data-testid={`admin-serie-foto-${idx}`}>
            <img src={f.previewUrl} alt={`Imagem ${idx + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <div style={{ position: "absolute", bottom: 4, left: 4, padding: "2px 6px", background: "rgba(0,0,0,0.7)", color: "var(--gold)", fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase", fontWeight: 600 }}>
              Imagem {idx + 1}
            </div>
            <button
              onClick={() => removerFoto(idx)}
              data-testid={`admin-serie-remover-${idx}`}
              style={{ position: "absolute", top: 4, right: 4, padding: 4, background: "rgba(0,0,0,0.7)", color: "white", border: 0, cursor: "pointer" }}
              title="Remover esta foto"
            >
              <X size={14} />
            </button>
          </div>
        ))}
        {fotos.length < 12 && (
          <button
            onClick={() => inputAdd.current?.click()}
            data-testid="admin-serie-add-foto"
            style={{
              aspectRatio: "1/1", border: "2px dashed var(--gold)",
              background: "rgba(184,147,90,0.05)", color: "var(--gold)",
              cursor: "pointer", display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", gap: 4,
            }}
          >
            <Camera size={22} />
            <span style={{ fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase" }}>
              {fotos.length === 0 ? "Adicionar foto" : "+ Mais"}
            </span>
          </button>
        )}
        <input
          ref={inputAdd}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          style={{ display: "none" }}
          onChange={handleAddFotos}
          data-testid="admin-serie-input-add"
        />
      </div>
      <div style={{ marginTop: 6, fontSize: 11, color: "var(--ink-3)" }}>
        {fotos.length} foto{fotos.length === 1 ? "" : "s"} selecionada{fotos.length === 1 ? "" : "s"} · pode adicionar até 12
      </div>
      {/* GPS opcional */}
      <div style={{ marginTop: 14, padding: 10, border: "1px solid var(--line)", background: "var(--bg-3)", fontSize: 12, color: "var(--ink-3)" }} data-testid="admin-gps-block">
        <MapPin size={12} style={{ display: "inline", verticalAlign: "middle", marginRight: 6, color: "var(--gold)" }} />
        {gpsBusy && "Capturando localização (opcional)…"}
        {!gpsBusy && gps && <span data-testid="admin-gps-endereco"><strong>📍 {gps.endereco}</strong> <span style={{ opacity: 0.6 }}>(opcional)</span></span>}
        {!gpsBusy && !gps && <span style={{ opacity: 0.8 }}>Localização não disponível (opcional para CAMASA).</span>}
      </div>
      {err && (
        <div style={{ marginTop: 12, padding: 10, background: "rgba(196,122,122,0.08)", border: "1px solid rgba(196,122,122,0.3)", color: "#d49595", fontSize: 13 }} data-testid="admin-serie-err">
          ⚠ {err}
        </div>
      )}
      <div style={{ marginTop: 14, padding: 10, background: "rgba(95,168,108,0.06)", border: "1px solid rgba(95,168,108,0.25)", fontSize: 12, color: "var(--ink-3)" }}>
        <CheckCircle2 size={12} style={{ display: "inline", verticalAlign: "middle", marginRight: 6, color: "#5fa86c" }} />
        Como você é a CAMASA, a série já entra <strong>aprovada</strong> automaticamente.
      </div>
      <div className="modal-foot" style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--line)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button className="btn btn-ghost" onClick={onCancel} disabled={busy}>Cancelar</button>
        <button
          className="btn"
          onClick={salvar}
          disabled={busy}
          data-testid="admin-serie-salvar"
          style={{ background: "var(--gold)", color: "#0a0a0a", fontWeight: 600 }}
        >
          {busy ? "Salvando…" : "Salvar série"}
        </button>
      </div>
    </div>
  );
}
